import { useCallback, useEffect, useRef, useState } from 'react'

export default function useIdleUser(timeout = 60000) {
  const [isUserActive, setIsUserActive] = useState(true)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const lastStateRef = useRef<boolean>(true)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  const goIdle = useCallback(() => {
    if (lastStateRef.current) {
      setIsUserActive(false)
      lastStateRef.current = false
    }
  }, [])

  const resetTimer = useCallback(() => {
    // Debounce to avoid over-triggering
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      if (!lastStateRef.current) {
        setIsUserActive(true)
        lastStateRef.current = true
      }

      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(goIdle, timeout)
    }, 500)
  }, [timeout, goIdle])

  // If user switches away or minimizes the tab, document.hidden becomes true → we immediately mark the user as idle
  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      goIdle()
    } else {
      resetTimer()
    }
  }, [goIdle, resetTimer])

  useEffect(() => {
    const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart', 'touchmove']
    events.forEach((event) => window.addEventListener(event, resetTimer))
    document.addEventListener('visibilitychange', handleVisibilityChange)

    resetTimer()

    return () => {
      events.forEach((event) => window.removeEventListener(event, resetTimer))
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (timerRef.current) clearTimeout(timerRef.current)
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [resetTimer, handleVisibilityChange])

  return { isUserActive }
}
